import { useMemo } from 'react'
import { format, isToday, parseISO } from 'date-fns'
import { cn, getEventColor } from '../../../lib/utils'

const HOUR_START = 6
const HOUR_END   = 23
const TOTAL_HRS  = HOUR_END - HOUR_START
const CELL_H     = 56

const formatTime = (iso) => iso ? format(parseISO(iso), 'h:mm a') : ''

export const DayView = ({ day, events, members, onEventClick }) => {
  const memberMap = useMemo(() => {
    const map = {}
    for (const m of members ?? []) {
      if (m.profiles) map[m.profiles.id] = m.profiles
    }
    return map
  }, [members])

  const dayKey = format(day, 'yyyy-MM-dd')

  const dayEvents = useMemo(() => {
    return events.filter((e) => (e.start_date ?? e.start_time)?.slice(0, 10) === dayKey)
  }, [events, dayKey])

  const allDayEvents  = dayEvents.filter((e) => e.is_all_day)
  const timedEvents   = dayEvents.filter((e) => !e.is_all_day && e.start_time)

  const positioned = useMemo(() => {
    return timedEvents.map((e) => {
      const start  = parseISO(e.start_time)
      const end    = e.end_time ? parseISO(e.end_time) : new Date(start.getTime() + 60 * 60 * 1000)
      const startMin = start.getHours() * 60 + start.getMinutes()
      const endMin   = end.getHours() * 60 + end.getMinutes()
      const top      = ((startMin / 60 - HOUR_START) / TOTAL_HRS) * (TOTAL_HRS * CELL_H)
      const height   = Math.max(((endMin - startMin) / 60 / TOTAL_HRS) * (TOTAL_HRS * CELL_H), 24)
      return { ...e, top, height }
    })
  }, [timedEvents])

  const hours = Array.from({ length: TOTAL_HRS }, (_, i) => HOUR_START + i)

  return (
    <div className="flex-1 overflow-auto">
      {/* All-day strip */}
      {allDayEvents.length > 0 && (
        <div className="border-b border-border px-4 py-2 bg-surface sticky top-0 z-10">
          <div className="flex flex-wrap gap-1.5">
            {allDayEvents.map((e) => (
              <button key={e._key ?? e.id} onClick={() => onEventClick(e)}
                className="text-xs font-medium px-2 py-1 rounded-lg text-white"
                style={{ backgroundColor: getEventColor(e) }}>
                {e.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Time grid */}
      <div className="relative" style={{ height: TOTAL_HRS * CELL_H }}>
        {/* Hour lines */}
        {hours.map((h) => (
          <div key={h} className="absolute left-0 right-0 flex items-start" style={{ top: (h - HOUR_START) * CELL_H }}>
            <span className="text-[10px] text-text-muted w-12 text-right pr-2 select-none -mt-2">
              {format(new Date(2000, 0, 1, h), 'h a')}
            </span>
            <div className="flex-1 border-t border-border/50" />
          </div>
        ))}

        {/* Events */}
        {positioned.map((e) => {
          const taggedMembers = (e.event_members ?? []).map((em) => memberMap[em.user_id]).filter(Boolean)
          return (
            <button
              key={e._key ?? e.id}
              onClick={() => onEventClick(e)}
              className="absolute left-14 right-3 rounded-lg px-2 py-1 text-left overflow-hidden hover:opacity-90 transition-opacity"
              style={{ top: e.top, height: e.height, backgroundColor: getEventColor(e) }}
            >
              <p className="text-xs font-semibold text-white leading-tight truncate">{e.title}</p>
              {e.height > 32 && (
                <p className="text-[10px] text-white/80 mt-0.5">
                  {formatTime(e.start_time)}{e.end_time && ` – ${formatTime(e.end_time)}`}
                </p>
              )}
              {e.height > 48 && taggedMembers.length > 0 && (
                <div className="flex gap-1 mt-1 flex-wrap">
                  {taggedMembers.map((p) => (
                    <span key={p.id} className="text-[9px] font-medium px-1.5 py-0.5 rounded-full text-white/90 bg-black/20">
                      {p.display_name}
                    </span>
                  ))}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Empty state for no events */}
      {dayEvents.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-sm text-text-muted">No events on {format(day, 'EEEE, MMM d')}</p>
        </div>
      )}
    </div>
  )
}
